import React, { useEffect, useState } from 'react';
import { Mic2, Linkedin, ExternalLink, Sparkles, Play, Globe, Volume2, VolumeX, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, fileUrl } from '@/lib/api';
import { AAWTA } from '@/lib/aawta';
import ShareMenu from './ShareMenu';

const buildSpeakerProfileUrl = (id) => {
  if (typeof window === 'undefined') return '';
  return `${window.location.origin}/#speaker-${id}`;
};

const buildSpeakerShareText = (s) => {
  const session = s.session_title || s.session_assignment;
  const parts = [s.name];
  if (s.title || s.organization) parts.push([s.title, s.organization].filter(Boolean).join(' · '));
  if (session) parts.push(session);
  if (s.session_time) parts.push(s.session_time);
  return parts.join(' · ');
};

const openExternal = (url) => url && window.open(url, '_blank', 'noopener,noreferrer');

const SpeakerBio = ({ bio, id }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = bio.length > 220;
  const preview = isLong ? bio.slice(0, 220).trimEnd() + '…' : bio;
  return (
    <div className="mt-3">
      <p
        data-testid={`speaker-bio-${id}`}
        className={`text-[13.5px] text-aawta-muted leading-relaxed whitespace-pre-wrap ${expanded ? 'max-h-[40vh] overflow-y-auto pr-1' : ''}`}
      >
        {expanded ? bio : preview}
      </p>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`speaker-readmore-${id}`}
          className="mt-1.5 inline-flex items-center gap-1 text-[12.5px] font-semibold text-aawta-magenta hover:text-aawta-magenta-hover"
        >
          {expanded ? (<>Read Less <ChevronUp className="h-3.5 w-3.5" /></>) : (<>Read More <ChevronDown className="h-3.5 w-3.5" /></>)}
        </button>
      )}
    </div>
  );
};

const normalizeUrl = (raw) => {
  if (!raw) return null;
  const v = String(raw).trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v}`;
};

const SpeakerCard = ({ s }) => {
  const [muted, setMuted] = useState(true);
  const [imgFailed, setImgFailed] = useState(false);
  const website = normalizeUrl(s.website);
  const linkedin = normalizeUrl(s.linkedin);
  const hasVideo = !!s.video_intro_file_id;
  const hasHeadshot = !!s.headshot_file_id && !imgFailed;

  return (
    <div
      id={`speaker-${s.id}`}
      data-testid={`speaker-card-${s.id}`}
      className="group rounded-2xl bg-aawta-surface border border-aawta-border overflow-hidden hover:shadow-aawta-elevated hover:-translate-y-1 transition-all duration-300 flex flex-col"
    >
      {/* Media: video prominent if available, headshot alongside/beneath */}
      {hasVideo ? (
        <div className="relative w-full aspect-[4/5] bg-black">
          <video
            src={fileUrl(s.video_intro_file_id)}
            controls
            playsInline
            muted={muted}
            preload="metadata"
            className="w-full h-full object-cover"
            data-testid={`speaker-video-${s.id}`}
          />
          <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-aawta-magenta text-white text-[10px] font-bold uppercase tracking-wider shadow-aawta-magenta">
            <Play className="h-2.5 w-2.5" fill="currentColor" /> Video
          </div>
          <button
            type="button"
            onClick={() => setMuted((m) => !m)}
            data-testid={`speaker-mute-${s.id}`}
            aria-label={muted ? 'Unmute' : 'Mute'}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/85 hover:bg-white text-aawta-navy flex items-center justify-center"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>
      ) : hasHeadshot ? (
        <button
          type="button"
          onClick={() => website ? openExternal(website) : openExternal(linkedin)}
          data-testid={`speaker-headshot-btn-${s.id}`}
          className="relative w-full aspect-[4/5] block bg-white text-left cursor-pointer"
          aria-label={website ? `Visit ${s.name} website` : s.name}
        >
          <img
            src={fileUrl(s.headshot_file_id)}
            alt={s.name}
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover"
            data-testid={`speaker-headshot-${s.id}`}
            loading="lazy"
          />
        </button>
      ) : (
        <div className="w-full aspect-[4/5] bg-gradient-to-br from-aawta-navy/5 to-aawta-magenta/10 flex items-center justify-center">
          <Mic2 className="h-14 w-14 text-aawta-navy/25" />
        </div>
      )}

      <div className="p-5 flex-1 flex flex-col">
        {/* If video is present AND headshot exists, show headshot as small circular avatar alongside the name */}
        <div className="flex items-start gap-3">
          {hasVideo && hasHeadshot && (
            <img
              src={fileUrl(s.headshot_file_id)}
              alt={`${s.name} portrait`}
              onError={() => setImgFailed(true)}
              className="shrink-0 w-14 h-14 rounded-full object-cover border-2 border-white shadow-aawta-soft -mt-11 relative z-10 bg-white"
              data-testid={`speaker-portrait-${s.id}`}
              loading="lazy"
            />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-lg text-aawta-navy">{s.name}</h3>
            <div className="text-[13px] text-aawta-muted">
              {[s.title, s.organization].filter(Boolean).join(' · ')}
            </div>
          </div>
        </div>

        {(s.session_title || s.session_assignment) && (
          <div className="mt-3 inline-flex self-start items-center px-2 py-0.5 rounded-full bg-aawta-magenta/10 text-aawta-magenta text-[10.5px] font-bold uppercase tracking-wider" data-testid={`speaker-session-${s.id}`}>
            {s.session_title || s.session_assignment}
          </div>
        )}
        {s.session_assignment && s.session_title && s.session_assignment !== s.session_title && (
          <div className="mt-1 text-[12.5px] text-aawta-navy font-medium">{s.session_assignment}</div>
        )}
        {s.session_time && <div className="mt-1 text-[12px] text-aawta-muted">{s.session_time}</div>}
        {s.biography && <SpeakerBio bio={s.biography} id={s.id} />}
        <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
          {website && (
            <button
              onClick={() => openExternal(website)}
              data-testid={`speaker-website-${s.id}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-aawta-navy hover:bg-aawta-magenta text-white text-[12px] font-semibold px-3 py-1.5 transition-colors"
            >
              <Globe className="h-3.5 w-3.5" /> Website
            </button>
          )}
          {linkedin && (
            <button
              onClick={() => openExternal(linkedin)}
              data-testid={`speaker-linkedin-${s.id}`}
              className="w-8 h-8 rounded-full bg-white border border-aawta-border flex items-center justify-center text-aawta-navy hover:bg-aawta-navy hover:text-white transition-colors"
              aria-label="LinkedIn"
            >
              <Linkedin className="h-4 w-4" />
            </button>
          )}
          <div className="ml-auto">
            <ShareMenu
              title={`${s.name} — AAWTA Workforce Summit Speaker`}
              text={buildSpeakerShareText(s)}
              url={buildSpeakerProfileUrl(s.id)}
              emailSubject={`${s.name} is speaking at the AAWTA Workforce Summit`}
              emailBody={`${buildSpeakerShareText(s)}\n\nView the speaker profile:`}
              testIdPrefix={`speaker-share-${s.id}`}
              compact
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const Speakers = () => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get('/speakers').then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  const has = items && items.length > 0;

  return (
    <section id="speakers" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div className="max-w-2xl">
            <span className="overline text-aawta-magenta">Speakers</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-4 text-aawta-navy tracking-tight leading-[1.1]">
              Meet Our Speakers
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              Discover the leaders, educators, employers, innovators, and workforce
              professionals helping shape the future of trucking, transportation, logistics,
              and supply chain.
            </p>
          </div>
          <Button
            onClick={() => openExternal(AAWTA.eventbriteUrl)}
            className="rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 py-6 shadow-aawta-magenta hover:-translate-y-0.5 transition-all"
          >
            Register to Attend
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {has ? (
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {items.map((s) => <SpeakerCard key={s.id} s={s} />)}
          </div>
        ) : (
          <div className="mt-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-aawta-magenta/10 text-aawta-magenta text-[11px] font-bold uppercase tracking-[0.18em] mb-6">
              <Sparkles className="h-3.5 w-3.5" /> Coming Soon
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-dashed border-aawta-border bg-aawta-surface p-5">
                  <div className="aspect-square rounded-xl bg-white border border-aawta-border flex items-center justify-center">
                    <Mic2 className="h-10 w-10 text-aawta-navy/25" />
                  </div>
                  <div className="mt-3 space-y-1.5">
                    <div className="h-2.5 rounded-full bg-aawta-border w-3/4" />
                    <div className="h-2 rounded-full bg-aawta-border w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Speakers;
